<script setup lang="ts">
import '@fortawesome/fontawesome-free/css/all.min.css'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { normalizeFieldBool, normalizeFieldText } from '~/composables/useCustomFields'

const siteData = await useSiteData()
const route = useRoute()
const navigationEpoch = useNavigationEpoch()
const fields = siteData?.setting?.customFields

const themeMode = normalizeFieldText(fields, 'theme', 'system').toLowerCase()
const siteName = normalizeFieldText(fields, 'siteName', 'Yuelai Blog')
const siteDescription = normalizeFieldText(fields, 'siteDescription', normalizeFieldText(fields, 'seoDescription', ''))
const seoKeywords = normalizeFieldText(fields, 'seoKeywords', '')
const seoImage = normalizeFieldText(fields, 'seoImage', '')
const siteFavicon = normalizeFieldText(fields, 'siteFavicon', '')
const umamiEnabled = normalizeFieldBool(fields, 'umamiEnabled', false)
const umamiHost = normalizeFieldText(fields, 'umamiHost', '')
const umamiWebsiteId = normalizeFieldText(fields, 'umamiWebsiteId', '')
const customCss = normalizeFieldText(fields, 'customCss', '')
const customJs = normalizeFieldText(fields, 'customJs', '')

const umamiScriptSrc = computed(() => {
  if (!umamiHost) {
    return ''
  }
  if (umamiHost.endsWith('.js')) {
    return umamiHost
  }
  return `${umamiHost.replace(/\/+$/, '')}/script.js`
})

const faviconHref = siteFavicon ? resolveImageAssetUrl(siteFavicon) : ''
const safeCustomJs = customJs.replace(/<\/script/gi, '<\\/script')

watch(
  () => `${route.path}?${JSON.stringify(route.query || {})}`,
  () => {
    navigationEpoch.value += 1
  },
  { immediate: true }
)

useSeoMeta({
  titleTemplate: titleChunk => (titleChunk ? `${titleChunk}` : siteName),
  description: siteDescription || undefined,
  keywords: seoKeywords || undefined,
  ogSiteName: siteName,
  ogDescription: siteDescription || undefined,
  ogImage: seoImage ? resolveImageAssetUrl(seoImage) : undefined,
  twitterCard: seoImage ? 'summary_large_image' : undefined,
  twitterImage: seoImage ? resolveImageAssetUrl(seoImage) : undefined
})

useHead(() => {
  const scripts: Array<
    | {
        defer: true
        src: string
        'data-website-id': string
      }
    | {
        key: string
        type: 'text/javascript'
        children: string
        tagPosition: 'bodyClose'
      }
  > = []
  if (umamiEnabled && umamiWebsiteId && umamiScriptSrc.value) {
    scripts.push({ defer: true, src: umamiScriptSrc.value, 'data-website-id': umamiWebsiteId })
  }
  if (safeCustomJs) {
    scripts.push({ key: 'theme-custom-js', type: 'text/javascript', children: safeCustomJs, tagPosition: 'bodyClose' })
  }

  return {
    link: faviconHref
      ? [
          { rel: 'icon', href: faviconHref },
          { rel: 'shortcut icon', href: faviconHref }
        ]
      : [],
    style: customCss ? [{ key: 'theme-custom-css', type: 'text/css', children: customCss }] : [],
    script: scripts
  }
})
</script>

<template>
  <NuxtLoadingIndicator color="#1f6feb" :height="3" />
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>
